<template>
  <div class="intercom-select-wrapper">
    <label v-if="label" :for="selectId" class="intercom-select-label">
      {{ label }}
      <span v-if="required" class="intercom-select-required">*</span>
    </label>

    <div :class="selectContainerClasses" ref="triggerContainer">
      <!-- Selected value display / trigger — ALWAYS rendered (never swapped out)
           so the click handler never fires on a detached element. The search
           input for filterable selects lives inside the teleported dropdown. -->
      <button
        ref="triggerButton"
        :id="selectId"
        type="button"
        class="intercom-select-trigger"
        :class="{ 'intercom-select-trigger--disabled': disabled }"
        :aria-disabled="disabled"
        @click="toggleDropdown"
        @keydown.escape.prevent="closeDropdown"
        @keydown.down.prevent="openDropdown"
        @keydown.up.prevent="openDropdown"
        @keydown.enter="handleTriggerEnter"
        @keydown.space.prevent="toggleDropdown"
      >
        <span v-if="selectedOption" class="intercom-select-value">
          <slot name="selected" :option="selectedOption">
            {{ selectedOption.label }}
          </slot>
        </span>
        <span v-else class="intercom-select-placeholder">
          {{ placeholder }}
        </span>

        <!-- Chevron icon -->
        <svg
          class="intercom-select-chevron"
          :class="{ 'intercom-select-chevron--open': isOpen }"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

    </div>

    <!-- Dropdown teleported to body to escape overflow clipping in modals -->
    <Teleport to="body">
      <Transition name="dropdown">
        <div
          v-if="isOpen"
          class="intercom-select-dropdown"
          ref="dropdown"
          :style="dropdownStyle"
          tabindex="-1"
          @keydown.escape.prevent="closeDropdown"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          @keydown.enter.prevent="selectHighlighted"
        >
          <!-- Search input — lives INSIDE the dropdown panel, not in the
               trigger, so the trigger button never gets unmounted during
               the click that opens the dropdown. -->
          <input
            v-if="filterable"
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="intercom-select-search"
            :placeholder="searchPlaceholder"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="isOpen"
            :aria-activedescendant="activeOptionId"
            :aria-controls="`${selectId}-listbox`"
            @input="handleSearch"
            @keydown.escape.prevent.stop="closeDropdown"
            @keydown.down.prevent.stop="highlightNext"
            @keydown.up.prevent.stop="highlightPrev"
            @keydown.enter.prevent.stop="selectHighlighted"
          />

          <div class="intercom-select-options-list" :id="`${selectId}-listbox`" role="listbox">
          <div
            v-for="(option, index) in filteredOptions"
            :key="option.value"
            :id="`${selectId}-option-${index}`"
            role="option"
            :aria-selected="isSelected(option)"
            :class="optionClasses(option, index)"
            @click="selectOption(option)"
            @mouseenter="highlightedIndex = index"
          >
            <slot name="option" :option="option">
              {{ option.label }}
            </slot>
            <!-- Check icon for selected -->
            <svg
              v-if="isSelected(option)"
              class="intercom-select-check"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>

          <!-- Empty state -->
          <div v-if="filteredOptions.length === 0" class="intercom-select-empty">
            {{ emptyText }}
          </div>
          </div><!-- /.intercom-select-options-list -->
        </div>
      </Transition>
    </Teleport>

    <p v-if="error" class="intercom-select-error">
      {{ error }}
    </p>
    <p v-else-if="hint" class="intercom-select-hint">
      {{ hint }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Object, null],
    default: null,
  },
  options: {
    type: Array,
    required: true,
    // Each option should have { value, label } at minimum
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select...',
  },
  searchPlaceholder: {
    type: String,
    default: 'Search...',
  },
  emptyText: {
    type: String,
    default: 'No options found',
  },
  error: {
    type: String,
    default: '',
  },
  hint: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  filterable: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  valueKey: {
    type: String,
    default: 'value',
  },
  labelKey: {
    type: String,
    default: 'label',
  },
})

const emit = defineEmits(['update:modelValue', 'change', 'search', 'open', 'close'])

// Global: close all other selects when one opens
const CLOSE_EVENT = 'intercom-select-close-all'

// State
const isOpen = ref(false)
const searchQuery = ref('')
const highlightedIndex = ref(-1)
const selectId = ref(`intercom-select-${Math.random().toString(36).substr(2, 9)}`)
const searchInput = ref(null)
const triggerButton = ref(null)
const dropdown = ref(null)
const triggerContainer = ref(null)
const triggerRect = reactive({ top: 0, left: 0, width: 0 })

// Computed
const selectedOption = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return null
  return props.options.find((opt) => {
    const optValue = typeof opt === 'object' ? opt[props.valueKey] : opt
    return optValue === props.modelValue
  })
})

const filteredOptions = computed(() => {
  if (!props.filterable || !searchQuery.value) {
    return props.options
  }
  const query = searchQuery.value.toLowerCase()
  return props.options.filter((opt) => {
    const label = typeof opt === 'object' ? opt[props.labelKey] : String(opt)
    return label.toLowerCase().includes(query)
  })
})

// Auto-highlight the first filtered option whenever the list changes while
// the dropdown is open. flush: 'sync' is critical here — without it, the
// watcher fires asynchronously, which means a fast typer pressing Enter
// immediately after the last character can hit selectHighlighted while
// highlightedIndex is still stale. Synchronous flush guarantees the index
// is up to date before any subsequent keydown handler reads it.
watch(
  filteredOptions,
  (newList) => {
    if (!isOpen.value) return
    highlightedIndex.value = newList.length > 0 ? 0 : -1
  },
  { flush: 'sync' }
)

// aria-activedescendant value for the combobox input — the id of the
// currently highlighted option, or undefined if no highlight. Lives on the
// textbox per WAI-ARIA APG so screen readers track focus without moving
// real DOM focus out of the input.
const activeOptionId = computed(() => {
  if (!isOpen.value || highlightedIndex.value < 0) return undefined
  if (highlightedIndex.value >= filteredOptions.value.length) return undefined
  return `${selectId.value}-option-${highlightedIndex.value}`
})

const selectContainerClasses = computed(() => [
  'intercom-select-container',
  {
    'intercom-select-container--open': isOpen.value,
    'intercom-select-container--error': props.error,
    'intercom-select-container--disabled': props.disabled,
  },
])

const dropdownStyle = computed(() => ({
  position: 'fixed',
  top: `${triggerRect.top}px`,
  left: `${triggerRect.left}px`,
  width: `${triggerRect.width}px`,
  zIndex: 9999,
}))

// Methods
const isOptionDisabled = (option) => typeof option === 'object' && option.disabled === true

const optionClasses = (option, index) => [
  'intercom-select-option',
  {
    'intercom-select-option--selected': isSelected(option),
    'intercom-select-option--highlighted': index === highlightedIndex.value,
    'intercom-select-option--disabled': isOptionDisabled(option),
  },
]

const isSelected = (option) => {
  const optValue = typeof option === 'object' ? option[props.valueKey] : option
  return optValue === props.modelValue
}

const toggleDropdown = () => {
  if (props.disabled) return
  isOpen.value ? closeDropdown() : openDropdown()
}

const openDropdown = () => {
  if (props.disabled || isOpen.value) return

  // Close all other open selects first
  window.dispatchEvent(new CustomEvent(CLOSE_EVENT, { detail: selectId.value }))

  // Compute fixed position from trigger before opening
  if (triggerContainer.value) {
    const rect = triggerContainer.value.getBoundingClientRect()
    triggerRect.top = rect.bottom + 4
    triggerRect.left = rect.left
    triggerRect.width = rect.width
  }

  isOpen.value = true
  searchQuery.value = ''
  justSelected = false // Reset on open
  emit('open')

  // Pre-highlight the currently selected option so Enter immediately confirms it
  if (props.modelValue !== null && props.modelValue !== undefined) {
    const idx = filteredOptions.value.findIndex((opt) => {
      const optValue = typeof opt === 'object' ? opt[props.valueKey] : opt
      return optValue === props.modelValue
    })
    highlightedIndex.value = idx >= 0 ? idx : 0
  } else {
    highlightedIndex.value = 0
  }

  nextTick(() => {
    if (props.filterable && searchInput.value) {
      searchInput.value.focus()
    } else if (dropdown.value) {
      dropdown.value.focus()
    }
    // Scroll the pre-highlighted option (currently-selected or first)
    // into view so the user can see where they are in the list.
    scrollHighlightedIntoView()
  })
}

const closeDropdown = () => {
  if (!isOpen.value) return
  isOpen.value = false
  searchQuery.value = ''
  emit('close')
}

const selectOption = (option) => {
  if (isOptionDisabled(option)) return
  const value = typeof option === 'object' ? option[props.valueKey] : option
  emit('update:modelValue', value)
  emit('change', option)
  closeDropdown()
  // Mark that user just made a selection — next Enter on trigger will advance
  justSelected = true
  // Focus the trigger button so the next Enter is caught
  nextTick(() => {
    if (triggerButton.value) triggerButton.value.focus()
  })
}

const handleSearch = () => {
  emit('search', searchQuery.value)
  highlightedIndex.value = 0
}

// After changing highlightedIndex, scroll the newly highlighted option into
// view — otherwise keyboard users pressing Down past the visible bottom (or
// Up past the top) see nothing happen because the highlighted row is offscreen.
const scrollHighlightedIntoView = () => {
  if (!dropdown.value || highlightedIndex.value < 0) return
  const options = dropdown.value.querySelectorAll('.intercom-select-option')
  const target = options[highlightedIndex.value]
  if (target && typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

const highlightNext = () => {
  if (highlightedIndex.value < filteredOptions.value.length - 1) {
    highlightedIndex.value++
    nextTick(scrollHighlightedIntoView)
  }
}

const highlightPrev = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
    nextTick(scrollHighlightedIntoView)
  }
}

// Track whether user just made a selection. After selecting an item from the
// dropdown, the next Enter should advance to the next field, not reopen.
let justSelected = false

// Enter on trigger button — 3-step flow:
// 1. Enter (dropdown closed, not just selected) → open dropdown
// 2. Enter (dropdown open) → handled by dropdown's @keydown.enter → selectHighlighted
// 3. Enter (dropdown closed, just selected) → let bubble to parent for advance
const handleTriggerEnter = (e) => {
  if (justSelected) {
    // Step 3: just selected an item — let Enter bubble for advance
    justSelected = false
    return
  }
  // Step 1: open the dropdown
  e.preventDefault()
  openDropdown()
}

const selectHighlighted = () => {
  // Read the live filtered list once, then resolve which option to commit.
  // The defensive layering is critical because of three independent edge
  // cases that real production users hit:
  //   (a) The async-clear race: if the parent uses @search with a server
  //       roundtrip and clears :options to [] while loading, filteredOptions
  //       transiently empties. Use props.options as a fallback so Enter
  //       still commits something visible to the user moments earlier.
  //   (b) The watch-flush race: even with flush:'sync', a downstream watcher
  //       in user code might delay the index update. Falling back to index 0
  //       is safe because that's what the auto-highlight intends.
  //   (c) IME composition / extension interception: 1Password etc. can
  //       suppress input events, leaving highlightedIndex at -1 even though
  //       the user clearly sees a match. Picking the first visible option
  //       matches user intent.
  const list = filteredOptions.value.length > 0 ? filteredOptions.value : props.options
  if (list.length === 0) return

  const idx =
    highlightedIndex.value >= 0 && highlightedIndex.value < list.length
      ? highlightedIndex.value
      : 0
  selectOption(list[idx])
}

defineExpose({
  /** Focus the trigger button (or search input if filterable and open). */
  focus() {
    if (isOpen.value && props.filterable && searchInput.value) {
      searchInput.value.focus()
    } else if (triggerButton.value) {
      triggerButton.value.focus()
    } else if (triggerContainer.value) {
      const btn = triggerContainer.value.querySelector('button, input')
      btn?.focus()
    }
  },
  /** Programmatically open the dropdown. */
  open: openDropdown,
  /** Programmatically close the dropdown. */
  close: closeDropdown,
})

// Click outside handler — must check both the wrapper and the teleported dropdown.
//
// We use event.composedPath() instead of event.target because, for filterable
// selects, the trigger <button> gets unmounted (v-if/v-else swap to <input>)
// during the same click that opens the dropdown. By the time this handler
// runs on the bubbled document-level click, event.target can be a detached
// node — wrapper.contains(detachedNode) returns false, incorrectly treating
// the click as "outside" and immediately closing the dropdown we just opened.
// composedPath() preserves the full original propagation path regardless of
// DOM mutations during the event.
const handleClickOutside = (event) => {
  const wrapper = triggerContainer.value?.closest('.intercom-select-wrapper')
  const path = typeof event.composedPath === 'function' ? event.composedPath() : []
  const inWrapper = wrapper ? path.includes(wrapper) || wrapper.contains(event.target) : false
  const inDropdown = dropdown.value
    ? path.includes(dropdown.value) || dropdown.value.contains(event.target)
    : false
  if (!inWrapper && !inDropdown) {
    closeDropdown()
  }
}

// Close this select when another one opens
const handleCloseAll = (event) => {
  if (event.detail !== selectId.value && isOpen.value) {
    closeDropdown()
  }
}

// Keep the teleported dropdown attached to the trigger when the page scrolls
// or the viewport resizes. Without this, `position: fixed` at the initial
// trigger rect leaves the dropdown stranded in its opening position while
// the trigger moves with the scroll — visually "detached."
const syncDropdownPosition = () => {
  if (!isOpen.value || !triggerContainer.value) return
  const rect = triggerContainer.value.getBoundingClientRect()
  triggerRect.top = rect.bottom + 4
  triggerRect.left = rect.left
  triggerRect.width = rect.width
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener(CLOSE_EVENT, handleCloseAll)
  // `true` captures scroll from nested scroll containers (settings-view, cards)
  // that don't bubble scroll events to window.
  window.addEventListener('scroll', syncDropdownPosition, true)
  window.addEventListener('resize', syncDropdownPosition)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener(CLOSE_EVENT, handleCloseAll)
  window.removeEventListener('scroll', syncDropdownPosition, true)
  window.removeEventListener('resize', syncDropdownPosition)
})
</script>

<style scoped>
/* ========================================
   INTERCOM SELECT BASE
   ======================================== */
.intercom-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  position: relative;
}

.intercom-select-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-700);
}

.intercom-select-required {
  color: var(--color-error);
  margin-left: 2px;
}

.intercom-select-container {
  position: relative;
}

/* ========================================
   TRIGGER BUTTON
   ======================================== */
.intercom-select-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  cursor: pointer;
  transition: all var(--transition-base);
  text-align: left;
}

.intercom-select-trigger:hover:not(:disabled) {
  border-color: var(--color-gray-300);
}

.intercom-select-trigger:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: var(--shadow-focus);
}

.intercom-select-container--open .intercom-select-trigger {
  border-color: var(--color-blue);
  box-shadow: var(--shadow-focus);
}

.intercom-select-container--error .intercom-select-trigger {
  background: var(--color-error-bg);
  border-color: var(--color-error);
}

.intercom-select-container--disabled .intercom-select-trigger {
  opacity: 0.5;
  cursor: not-allowed;
}

.intercom-select-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.intercom-select-placeholder {
  flex: 1;
  color: var(--color-gray-400);
}

.intercom-select-chevron {
  flex-shrink: 0;
  color: var(--color-gray-400);
  transition: transform var(--transition-fast);
}

.intercom-select-chevron--open {
  transform: rotate(180deg);
}

/* ========================================
   SEARCH INPUT
   ======================================== */
.intercom-select-search {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: var(--color-white);
  border: 0;
  border-bottom: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  outline: none;
  flex-shrink: 0;
  box-sizing: border-box;
}

.intercom-select-search:focus {
  border-bottom-color: var(--color-blue);
}

.intercom-select-search::placeholder {
  color: var(--color-gray-400);
}

/* ========================================
   DROPDOWN
   ======================================== */
.intercom-select-dropdown {
  /* position/top/left/width/z-index set via inline style (Teleport fixed positioning) */
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  max-height: 300px;
}

/* The options list scrolls — keeps the search input pinned at the top when
   filterable=true. max-height lets the panel grow to fit its search bar
   without exceeding the viewport. */
.intercom-select-options-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.intercom-select-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.intercom-select-option:hover {
  background: var(--color-gray-50);
}

/* Active/keyboard-highlighted option — distinct visual state so users can see
   where Enter will commit. Matches react-select / Naive UI / cmdk convention:
   subtle blue tint that stands out from the plain hover gray. */
.intercom-select-option--highlighted {
  background: var(--color-blue-light, #eff6ff);
  color: var(--color-blue, #2563eb);
}

.intercom-select-option--selected {
  background: var(--color-blue-light);
  color: var(--color-blue);
  font-weight: var(--font-weight-medium);
}

.intercom-select-option--selected:hover {
  background: var(--color-blue-light);
}

.intercom-select-option--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.intercom-select-option--disabled:hover {
  background: transparent;
}

.intercom-select-check {
  flex-shrink: 0;
  color: var(--color-blue);
}

.intercom-select-empty {
  padding: var(--space-4);
  text-align: center;
  color: var(--color-gray-400);
  font-size: var(--font-size-sm);
}

/* ========================================
   MESSAGES
   ======================================== */
.intercom-select-error {
  font-size: var(--font-size-sm);
  color: var(--color-error);
  margin: 0;
}

.intercom-select-hint {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0;
}

/* ========================================
   TRANSITIONS
   ======================================== */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
